import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEMO_PLOEG,
  DEMO_PLOEG_ID,
  DEMO_PRICELIST,
  DEMO_PRICELIST_ID,
  DEMO_META,
  DEMO_PARSED_ITEMS,
  DEMO_MATCHED_LINES,
  DEMO_MEERWERK_LINES,
  DEMO_SKIPPED_ITEMS,
  DEMO_CHARGE_LINES,
  DEMO_PARSE_RESULT,
} from '../lib/tourDemo';
import { AppShell } from '../components/AppShell';
import { api, ApiError, type ExtraOfferteSamenvatting, type PloegMetPrijslijsten } from '../lib/api';
import { useToast } from '../components/Toast';
import { fmtEUR, fmtNum, normalizeProjectNummer } from '../lib/format';
import { computeTotals, runMatching, learningToMatchingOptions, isGevelCalculatie, lineSubtotaal, valideerExtraRegel, USER_SKIP_REDEN, type ParsedItem, type MatchResult, type MatchedLine, type MatchedSubItem, type MeerwerkLine, type SkippedItem, type ExtraRegel, type GevelConfig, type GevelVariant, type Pricelist, type PricelistItem, type TotalsMetGevel } from '@recotex/shared';
import { useAuth } from '../lib/auth';
import { GevelBlock } from '../components/GevelBlock';
import { GevelVariantModal } from '../components/GevelVariantModal';
import { ExtraRegelsSection } from '../components/ExtraRegelsSection';
import { GelinkteProjectenEffect, PctWaarde, useLivePercentages } from '../components/LivePercentage';
import { OverlapVragen, beslisOverlap } from '../components/OverlapVragen';
import { useOverlapSync } from '../lib/overlap';
import { BevestigVragenKaart, BevestigVragenModal, telVragen, type VragenStaat } from '../components/BevestigVragen';
import type { ExtraInfoVraag, ExtraOfferteBeslissing, FicheBron } from '@recotex/shared';

/** Prijslijst + geheugen ophalen en matchen — zelfde geheugen als /parse op de server. */
async function matchWithLearning(pricelistId: string, items: ParsedItem[]) {
  const [detail, learning] = await Promise.all([
    api.pricelists.detail(pricelistId),
    api.pricelists.learned(pricelistId).catch(() => null),
  ]);
  const matched = runMatching(items, detail.pricelist, learningToMatchingOptions(learning, detail.pricelist));
  return { pricelist: detail.pricelist, matched };
}
import { PricelistItemPicker } from '../components/PricelistItemPicker';
import { PdfViewerPanel } from '../components/PdfViewerPanel';
import { PrijsafwijkingenBlok, afwijkingenZonderReden, useLiveAfwijkingen, zetReden, type LiveAfwijking } from '../components/CalculatieExtras';
import {
  BouwflowProjectPicker,
  BouwflowSubcontractorGroupPicker,
  type BouwflowProjectSelection,
  type BouwflowGroupSelection,
} from '../components/BouwflowPicker';

interface TourBridge {
  activateDemo: () => void;
  simulateNoPloeg: () => void;
  restoreDemoPloeg: () => void;
  simulateNoProjectNummer: () => void;
  restoreProjectNummer: () => void;
  goToStep3: () => void;
  deactivateDemo: () => void;
}

interface ParseResult {
  meta: { klantNaam: string; klantAdres: string; adviseur: string; projectTitel: string; projectNummer?: string; teamNaam?: string; opmerkingen?: string };
  items: ParsedItem[];
  matching: MatchResult;
  totals: { subtotaal: number; meerwerk: number; totaal: number };
  ploeg: { id: string; team: string; firma: string } | null;
  pricelistId: string | null;
  /** Voorstel gevelprijslijst (standaard van de onderaannemer, nog niet bevestigd). */
  gevel?: GevelConfig | null;
  gevelStandaard?: GevelVariant;
  pdfFilename: string;
  pdfStorageKey: string | null;
  detected?: {
    ploegFromTeamName: boolean;
    ploegResolvedVia: 'explicit' | 'team-name' | 'bouwflow-sheet' | null;
    extractedTeamNaam: string;
    extractedProjectNummer: string;
    bouwflowProject: { id: number; projectId: string; name: string; budget: number | null; offertewaarde?: number | null } | null;
    bouwflowSheetGroupId: number | null;
    bouwflowSheetGroupName: string | null;
    /** Onderaannemer op de fiche wijkt af van Bouwflow (2.1). */
    onderaannemerVerschil?: { fiche: string; bouwflow: string } | null;
  };
  /** Blok C deel 2: extra offertes (4), gebruikte fiche (7.3), extra informatie (SPEC_calculatie 8). */
  extraOffertes?: ExtraOfferteSamenvatting[];
  ficheBron?: FicheBron | null;
  extraInfoVragen?: ExtraInfoVraag[];
}

export function ProjectNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const pricelistsQ = useQuery({ queryKey: ['pricelists'], queryFn: () => api.pricelists.list() });
  const bouwflowStatusQ = useQuery({ queryKey: ['bouwflow-status'], queryFn: () => api.bouwflow.status() });
  const bouwflowEnabled = bouwflowStatusQ.data?.configured === true;
  const [bouwflowProject, setBouwflowProject] = useState<BouwflowProjectSelection | null>(null);
  const [bouwflowGroup, setBouwflowGroup] = useState<BouwflowGroupSelection | null>(null);
  const [manualMode, setManualMode] = useState(false);
  /** Bron van de opstartfiche: rechtstreeks uit Bouwflow of een opgeladen PDF. */
  const [bron, setBron] = useState<'bouwflow' | 'pdf'>('bouwflow');
  const fichebron = bouwflowEnabled ? bron : 'pdf';

  // === Pricelist met items voor de geselecteerde ploeg ===
  // De lijst-endpoint geeft pricelists terug zonder items; we hebben items nodig zodra de
  // gebruiker handmatig wil matchen of werkposten wil toevoegen in stap 3.
  // We laden de details daarom apart zodra ploeg gekozen is.

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedPloegId, setSelectedPloegId] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [editedItems, setEditedItems] = useState<ParsedItem[]>([]);
  const [matchedLines, setMatchedLines] = useState<MatchedLine[]>([]);
  const [meerwerkLines, setMeerwerkLines] = useState<MeerwerkLine[]>([]);
  const [skippedItems, setSkippedItems] = useState<SkippedItem[]>([]);
  const [extraRegels, setExtraRegels] = useState<ExtraRegel[]>([]);
  const [gevel, setGevel] = useState<GevelConfig | null>(null);
  const [gevelModal, setGevelModal] = useState<null | 'kies' | 'verversen'>(null);
  const [toonFouten, setToonFouten] = useState(false);
  // Blok C deel 2: extra offertes (4), extra informatie (SPEC_calculatie 8) en eenheden (8.1).
  const [extraOffertes, setExtraOffertes] = useState<ExtraOfferteBeslissing[]>([]);
  const [extraInfoVragen, setExtraInfoVragen] = useState<ExtraInfoVraag[]>([]);
  const [extraInfoGenegeerd, setExtraInfoGenegeerd] = useState<string[]>([]);
  const [vragenModal, setVragenModal] = useState(false);
  const vragenAutoRef = useRef(false);
  const { user } = useAuth();
  // Pop-ups bevestigen: enkel Eranda, Timen, Ervin of Jordy (SPEC_wijzigingen 6).
  const magBevestigen = !!user?.magBevestigen;
  const [meta, setMeta] = useState({ klantNaam: '', klantAdres: '', adviseur: '', projectTitel: '', projectNummer: '', opmerkingen: '' });
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  // Ref naar de input-row die net handmatig is toegevoegd, zodat we naar onder
  // scrollen en focussen — anders is "+ Item handmatig toevoegen" verwarrend
  // (het item verschijnt dan onderaan zonder feedback).
  const lastManualItemIdRef = useRef<string | null>(null);
  const tableEndRef = useRef<HTMLDivElement | null>(null);

  const ploegen = ploegenQ.data?.ploegen ?? [];
  const pricelists = pricelistsQ.data?.pricelists ?? [];
  const ploeg = ploegen.find((p) => p.id === selectedPloegId);
  // De prijslijst die bij /parse gebruikt werd (bv. per type werken: Nedelco hellend dak), zolang
  // het dezelfde ploeg is; anders de algemene lijst van de gekozen ploeg.
  const actievePricelistId = parseResult?.ploeg?.id === selectedPloegId && parseResult?.pricelistId ? parseResult.pricelistId : ploeg?.pricelistId;
  const pricelistDetailQ = useQuery({
    queryKey: ['pricelist', actievePricelistId],
    queryFn: () => api.pricelists.detail(actievePricelistId!),
    enabled: !!actievePricelistId,
  });
  const pricelist = pricelistDetailQ.data?.pricelist ?? null;
  const learningQ = useQuery({
    queryKey: ['learning', actievePricelistId],
    queryFn: () => api.pricelists.learned(actievePricelistId!),
    enabled: !!actievePricelistId,
  });

  const ploegenByFirma = useMemo(() => {
    const grp: Record<string, typeof ploegen> = {};
    for (const p of ploegen) (grp[p.firma] ||= []).push(p);
    return grp;
  }, [ploegen]);

  // === Stap 1: parse PDF ===
  // Ploeg en projectnummer worden door de AI uit de PDF gehaald en daarna
  // server-side geresolved. De gebruiker hoeft vooraf niets te selecteren —
  // tenzij hij/zij dat expliciet wil overrulen via de "Geavanceerd"-toggle.
  const verwerkParseResultaat = (result: ParseResult, viaBouwflow: boolean) => {
    setParseResult(result);
    // Auto-selecteer gedetecteerde ploeg (als er één gevonden is en de
    // gebruiker er nog geen had).
    if (result.ploeg && !selectedPloegId) {
      setSelectedPloegId(result.ploeg.id);
    }
    // Auto-vul projectnummer + Bouwflow projectId
    const det = result.detected;
    const autoProjectNummer = det?.bouwflowProject?.projectId
      || (det?.extractedProjectNummer ? normalizeProjectNummer(det.extractedProjectNummer) : '');
    const autoProjectTitel = det?.bouwflowProject?.name ?? '';
    if (det?.bouwflowProject) {
      setBouwflowProject({ id: det.bouwflowProject.id, projectId: det.bouwflowProject.projectId, name: det.bouwflowProject.name });
    }
    setMeta((prev) => ({
      ...result.meta,
      projectNummer: prev.projectNummer || autoProjectNummer,
      projectTitel: result.meta.projectTitel || autoProjectTitel,
      opmerkingen: result.meta.opmerkingen ?? '',
    }));
    // Toast — afzonderlijk feedback voor project en ploeg.
    const parts: string[] = [];
    if (det?.bouwflowProject) parts.push(`project ${det.bouwflowProject.projectId}`);
    else if (det?.extractedProjectNummer) parts.push(`project ${det.extractedProjectNummer} (niet in Bouwflow)`);
    if (result.ploeg) {
      const via = det?.ploegResolvedVia === 'bouwflow-sheet'
        ? (viaBouwflow ? '' : ' (uit Bouwflow opstartfiche)')
        : det?.ploegResolvedVia === 'team-name'
          ? ' (uit PDF teamnaam)'
          : '';
      parts.push(`ploeg ${result.ploeg.team}${via}`);
    } else if (det?.bouwflowSheetGroupName) {
      parts.push(`Bouwflow-ploeg "${det.bouwflowSheetGroupName}" (niet gemapt aan lokale ploeg)`);
    }
    if (parts.length > 0) {
      toast.success(`${viaBouwflow ? 'Opstartfiche uit Bouwflow' : 'Gedetecteerd'}: ${parts.join(' + ')}`);
    } else if (det?.extractedTeamNaam) {
      toast.warning(`"${det.extractedTeamNaam}" niet herkend als ploeg — kies handmatig`);
    } else {
      toast.warning('Niets automatisch herkend — vul in stap 2 handmatig aan');
    }
    // Werkelijk=0 is een GELDIGE waarde (item niet uitgevoerd) — niet mirroren naar geadviseerd.
    // Gebruiker kan handmatig aanpassen als de fiche slechts één kolom had.
    setEditedItems(result.items);
    setMatchedLines(result.matching.matchedLines);
    setMeerwerkLines(result.matching.meerwerkLines);
    setSkippedItems(result.matching.skippedItems);
    setGevel(result.gevel ?? null);
    setExtraOffertes((result.extraOffertes ?? []).map((e) => e.offerte));
    setExtraInfoVragen(result.extraInfoVragen ?? []);
    setExtraInfoGenegeerd([]);
    vragenAutoRef.current = false;
    if (det?.onderaannemerVerschil) toast.warning(`Onderaannemer op de fiche (${det.onderaannemerVerschil.fiche}) wijkt af van Bouwflow (${det.onderaannemerVerschil.bouwflow}). De fiche geldt.`);
    setStep(2);
  };

  const foutVan = (e: unknown, standaard: string) =>
    e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'error' in e.payload
      ? String((e.payload as { error: string }).error)
      : standaard;

  const handleParse = async () => {
    if (!pdfFile) return;
    setParsing(true);
    try {
      verwerkParseResultaat(await api.projects.parse(pdfFile, selectedPloegId || undefined), false);
    } catch (e) {
      toast.error(foutVan(e, 'AI-parsing mislukt'));
    } finally {
      setParsing(false);
    }
  };

  // Opstartfiche rechtstreeks uit Bouwflow (blok C, SPEC_workflow 2): geen PDF downloaden en opladen.
  const handleParseBouwflow = async (bouwflowProjectId: number) => {
    setParsing(true);
    try {
      verwerkParseResultaat(await api.projects.parseBouwflow(bouwflowProjectId, selectedPloegId || undefined), true);
    } catch (e) {
      toast.error(foutVan(e, 'Opstartfiche ophalen uit Bouwflow mislukt'));
    } finally {
      setParsing(false);
    }
  };

  // Vanuit "Te doen": /project/new?bouwflow=<id> haalt de fiche meteen op.
  const [zoekParams] = useSearchParams();
  const bouwflowParam = Number(zoekParams.get('bouwflow')) || null;
  const bouwflowParamGestart = useRef(false);
  useEffect(() => {
    if (!bouwflowParam || !bouwflowEnabled || bouwflowParamGestart.current) return;
    bouwflowParamGestart.current = true;
    setBron('bouwflow');
    void handleParseBouwflow(bouwflowParam);
  }, [bouwflowParam, bouwflowEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGevel = isGevelCalculatie(matchedLines, meerwerkLines);
  const gevelStandaard: GevelVariant = parseResult?.ploeg?.id === selectedPloegId && parseResult?.gevelStandaard ? parseResult.gevelStandaard : (ploeg?.gevelVariant ?? 'A');
  const totals = useMemo(
    () => computeTotals({ matchedLines, meerwerkLines, extraRegels, gevel: isGevel ? gevel : null }),
    [matchedLines, meerwerkLines, extraRegels, gevel, isGevel],
  );
  useOverlapSync(matchedLines, setMatchedLines, pricelist?.items ?? [], learningQ.data?.overlaps, step === 3);
  // Prijsafwijkingen t.o.v. de standaardprijs van de prijslijst van deze onderaannemer (27okt 2).
  const afwijkingen = useLiveAfwijkingen({ matchedLines, meerwerkLines, pricelistItems: pricelist?.items ?? [], gevel: isGevel ? gevel : null });
  const live = useLivePercentages({
    projectNummer: meta.projectNummer,
    werkpostenEnMeerwerk: totals.subtotaal + totals.meerwerk,
    extraRegels,
    // Offertewaarde = aanvaarde offertes in Bouwflow (niet `budget`, dat is enkel de laatste offerte).
    eigenNoemer: { noemer: parseResult?.detected?.bouwflowProject?.offertewaarde ?? null, basis: parseResult?.detected?.bouwflowProject?.offertewaarde ? 'offertewaarde' : null },
  });

  // Na een ploeg-wissel is er nog geen gevelvoorstel: laad de standaard van de nieuwe ploeg zodat
  // de getoonde totalen gelijk zijn aan wat de server bij het opslaan rekent.
  useEffect(() => {
    if (step !== 3 || !isGevel || gevel || !selectedPloegId) return;
    let actief = true;
    api.projects.gevelConfig(gevelStandaard, selectedPloegId)
      .then((r) => { if (actief) setGevel((huidig) => huidig ?? r.config); })
      .catch(() => { /* zonder voorstel blijft de pop-up de keuze vragen */ });
    return () => { actief = false; };
  }, [step, isGevel, gevel, selectedPloegId, gevelStandaard]);

  // Pop-up met de bevestigingsvragen (4, 8, 8.1): bij het openen van stap 3, na de keuze A/B.
  const vragenStaat: VragenStaat = { matchedLines, meerwerkLines, extraOffertes, extraInfoVragen, genegeerd: extraInfoGenegeerd };
  useEffect(() => {
    if (step !== 3 || gevelModal || vragenAutoRef.current || !magBevestigen) return;
    if (telVragen(vragenStaat).totaal > 0) {
      vragenAutoRef.current = true;
      setVragenModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, gevelModal, magBevestigen]);

  // Pop-up A/B bij het aanmaken van een gevelcalculatie (1.3): bij het openen van stap 3.
  useEffect(() => {
    if (step === 3 && isGevel && !gevel?.bevestigd && !gevelModal) setGevelModal('kies');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isGevel]);

  const handleSave = async (status: 'draft' | 'approved') => {
    if (!parseResult || !ploeg) return;
    if (extraRegels.some((r) => valideerExtraRegel(r).length > 0)) {
      setToonFouten(true);
      toast.error('Vul de extra werken en aanrekenbare kosten volledig in (omschrijving, bedrag, telt mee, reden).');
      return;
    }
    // Een prijs die afwijkt van de standaardprijs vraagt een reden (27okt 2).
    const zonderReden = afwijkingenZonderReden(afwijkingen);
    if (zonderReden.length) {
      setToonFouten(true);
      toast.error(`Geef een reden voor de afwijkende prijs van ${zonderReden.map((a) => a.post).join(', ')} (blok Prijsafwijkingen).`);
      return;
    }
    try {
      const resp = await api.projects.create({
        klantNaam: meta.klantNaam,
        klantAdres: meta.klantAdres,
        adviseur: meta.adviseur,
        projectTitel: meta.projectTitel,
        projectNummer: meta.projectNummer,
        opmerkingen: meta.opmerkingen,
        ploegId: ploeg.id,
        pdfFilename: parseResult.pdfFilename,
        pdfStorageKey: parseResult.pdfStorageKey ?? undefined,
        bouwflowProjectId: bouwflowProject?.id ?? null,
        offertewaarde: parseResult.detected?.bouwflowProject?.offertewaarde ?? null,
        parsedItems: editedItems,
        matchedLines,
        meerwerkLines,
        skippedItems,
        chargeLines: [],
        extraRegels,
        gevel: isGevel && gevel ? { variant: gevel.variant, pricelistId: gevel.pricelistId, bevestigd: gevel.bevestigd } : null,
        pricelistId: actievePricelistId,
        status,
        ficheBron: parseResult.ficheBron ?? null,
        extraOffertes,
        ...(extraInfoGenegeerd.length ? { extraInfoGenegeerd } : {}),
      });
      toast.success(status === 'approved' ? 'Project goedgekeurd' : 'Concept opgeslagen');
      navigate(`/project/${resp.id}`);
    } catch (e) {
      const payload = e instanceof ApiError ? (e.payload as { error?: string; details?: unknown } | null) : null;
      const detail = payload?.error
        ? payload.error + (Array.isArray(payload.details) ? ': ' + payload.details.join('; ') : '')
        : (e instanceof Error ? e.message : 'onbekende fout');
      console.error('[save] Fout:', detail);
      toast.error('Opslaan mislukt: ' + detail);
    }
  };

  // === Stap 2 helpers ===
  const updateItem = (id: string, field: keyof ParsedItem, value: string | number) => {
    setEditedItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };
  const removeItem = (id: string) => {
    setEditedItems((prev) => prev.filter((it) => it.id !== id));
  };
  /**
   * Voeg een handmatig item toe aan de geparseerde lijst — handig wanneer de AI iets
   * gemist heeft. Het item krijgt een `pi_manual_` prefix zodat stap 3 het automatisch
   * in de review-queue plaatst (de matcher heeft het nog niet gezien).
   */
  const addManualItem = () => {
    const id = 'pi_manual_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
    const newItem: ParsedItem = {
      id,
      categorie: 'HANDMATIG TOEGEVOEGD',
      werken: '',
      geadviseerdeHoeveelheid: 0,
      werkelijkeHoeveelheid: 0,
      eenheid: 'STUK',
    };
    lastManualItemIdRef.current = id;
    setEditedItems((prev) => [...prev, newItem]);
  };

  // === Tour-demo hook ===
  // Stelt de fake "PDF geüpload + AI klaar"-state in zodat de rondleiding
  // stap 2 + stap 3 kan tonen met representatieve data. window.__recotexTour
  // wordt door tour.ts aangesproken bij specifieke step-transitions.
  useEffect(() => {
    const activate = () => {
      qc.setQueryData(['ploegen'], {
        ploegen: [DEMO_PLOEG, ...((ploegenQ.data?.ploegen ?? []) as Array<{ id: string }>)],
      });
      qc.setQueryData(['pricelist', DEMO_PRICELIST_ID], { pricelist: DEMO_PRICELIST });
      setSelectedPloegId(DEMO_PLOEG_ID);
      setPdfFile(new File(['demo'], 'demo-opstartfiche.pdf', { type: 'application/pdf' }));
      setParseResult(DEMO_PARSE_RESULT);
      setMeta({ ...DEMO_META });
      setEditedItems(DEMO_PARSED_ITEMS);
      setMatchedLines(DEMO_MATCHED_LINES);
      setMeerwerkLines(DEMO_MEERWERK_LINES);
      setSkippedItems(DEMO_SKIPPED_ITEMS);
      setExtraRegels(DEMO_CHARGE_LINES.map((c) => ({
        id: c.id, soort: 'aanrekenbare_kost' as const, categorie: c.categorie, omschrijving: c.omschrijving, bedrag: c.bedrag,
        toegerekendProject: '', teltMeeInPercentage: true, bron: 'manueel' as const, bronReferentie: null, reden: c.omschrijving, oorspronkelijkePloegId: null,
      })));
      setStep(2);
    };

    // Simuleer "AI ploeg niet gevonden, maar Bouwflow toonde een group op de
    // opstartfiche". Toont de gele waarschuwingsbanner met koppel-dropdown.
    const simulateNoPloeg = () => {
      setSelectedPloegId('');
      setParseResult({
        ...DEMO_PARSE_RESULT,
        ploeg: null,
        pricelistId: null,
        detected: {
          ploegFromTeamName: false,
          ploegResolvedVia: 'bouwflow-sheet',
          extractedTeamNaam: 'DEMO',
          extractedProjectNummer: 'P-DEMO',
          bouwflowProject: { id: 9999, projectId: 'P-DEMO', name: 'Demo project — Hellend dak renovatie', budget: null },
          bouwflowSheetGroupId: 9999,
          bouwflowSheetGroupName: 'DEMO {Edrin}',
        },
      });
    };
    const restoreDemoPloeg = () => {
      setSelectedPloegId(DEMO_PLOEG_ID);
      setParseResult(DEMO_PARSE_RESULT);
    };

    // Simuleer "AI projectnummer niet gevonden" — projectnummer-veld is leeg.
    const simulateNoProjectNummer = () => {
      setMeta((prev) => ({ ...prev, projectNummer: '' }));
    };
    const restoreProjectNummer = () => {
      setMeta((prev) => ({ ...prev, projectNummer: 'P-DEMO' }));
    };

    const goToStep3 = () => setStep(3);
    const deactivate = () => {
      setSelectedPloegId('');
      setPdfFile(null);
      setParseResult(null);
      setMeta({ klantNaam: '', klantAdres: '', adviseur: '', projectTitel: '', projectNummer: '', opmerkingen: '' });
      setEditedItems([]);
      setMatchedLines([]);
      setMeerwerkLines([]);
      setSkippedItems([]);
      setExtraRegels([]);
      setGevel(null);
      setBouwflowProject(null);
      setBouwflowGroup(null);
      setStep(1);
      qc.invalidateQueries({ queryKey: ['ploegen'] });
      qc.removeQueries({ queryKey: ['pricelist', DEMO_PRICELIST_ID] });
    };
    (window as unknown as { __recotexTour?: TourBridge }).__recotexTour = {
      activateDemo: activate,
      simulateNoPloeg,
      restoreDemoPloeg,
      simulateNoProjectNummer,
      restoreProjectNummer,
      goToStep3,
      deactivateDemo: deactivate,
    };
    return () => {
      // Belangrijk: bij unmount expliciet de demo-cache opruimen (zelfs als de
      // tour niet correct werd afgesloten via ESC). Anders blijven DEMO_PLOEG +
      // DEMO_PRICELIST in react-query staan en lekken ze in dropdowns elders
      // (Onderaannemers-lijst, ploegen-pickers, etc.).
      qc.invalidateQueries({ queryKey: ['ploegen'] });
      qc.removeQueries({ queryKey: ['pricelist', DEMO_PRICELIST_ID] });
      delete (window as unknown as { __recotexTour?: unknown }).__recotexTour;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll + focus naar het zojuist toegevoegde handmatige item.
  useEffect(() => {
    const id = lastManualItemIdRef.current;
    if (!id) return;
    // Kort wachten zodat React de DOM heeft geüpdatet.
    requestAnimationFrame(() => {
      const row = document.getElementById(`parsed-item-${id}`);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const firstInput = row.querySelector('input');
        firstInput?.focus();
      } else {
        tableEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
      lastManualItemIdRef.current = null;
    });
  }, [editedItems]);

  const itemsByCategorie = useMemo(() => {
    const grp = new Map<string, ParsedItem[]>();
    for (const it of editedItems) {
      const k = it.categorie || '(geen categorie)';
      if (!grp.has(k)) grp.set(k, []);
      grp.get(k)!.push(it);
    }
    return grp;
  }, [editedItems]);

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">Nieuw project</h1>
          <p className="text-secondary text-sm mt-1">Haal de opstartfiche uit Bouwflow of laad ze op als PDF; de tool maakt de calculatie</p>
        </div>
        <div className="flex gap-2">
          {parseResult?.pdfStorageKey && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPdfViewerOpen((v) => !v)}
              title="Open de opstartfiche naast de calculatie voor visuele check"
            >
              {pdfViewerOpen ? '✕ Sluit fiche' : '👁 Bekijk opstartfiche'}
            </button>
          )}
          <Link to="/" className="btn-ghost">Annuleren</Link>
        </div>
      </div>

      {parseResult?.pdfStorageKey && (
        <PdfViewerPanel
          url={api.projects.tempPdfUrl(parseResult.pdfStorageKey)}
          open={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          title="Opstartfiche — bron"
        />
      )}

      {/* Stappen-indicator */}
      <div className="flex items-center gap-3 mb-6">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`flex items-center gap-2 ${step === n ? 'text-primary' : step > n ? 'text-success' : 'text-secondary'}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              step === n ? 'bg-accent text-white' : step > n ? 'bg-success text-white' : 'bg-warmgrey'
            }`}>
              {step > n ? '✓' : n}
            </span>
            <span className="text-xs font-medium">
              {n === 1 ? 'Opstartfiche' : n === 2 ? 'Posten nakijken' : 'Matching review'}
            </span>
            {n < 3 && <span className="w-8 h-0.5 bg-warmgrey mx-2" />}
          </div>
        ))}
      </div>

      {/* === STAP 1 === */}
      {step === 1 && (
        <div className="card max-w-3xl">
          <h2 className="text-xl font-bold mb-1">Stap 1 — {fichebron === 'bouwflow' ? 'Kies het project in Bouwflow' : 'Upload de opstartfiche'}</h2>
          <p className="text-sm text-secondary mb-4">
            {fichebron === 'bouwflow'
              ? 'De tool haalt de meest recente opstartfiche rechtstreeks uit Bouwflow: posten, werkelijke hoeveelheden, onderaannemer, klant en werfadres.'
              : 'Ploeg en projectnummer worden automatisch herkend uit de PDF. Je hoeft niets vooraf in te vullen.'}
          </p>

          {bouwflowEnabled && (
            <div className="segmented mb-5" role="group" aria-label="Bron van de opstartfiche">
              <button type="button" className="segmented-item" aria-pressed={bron === 'bouwflow'} onClick={() => setBron('bouwflow')}>Uit Bouwflow</button>
              <button type="button" className="segmented-item" aria-pressed={bron === 'pdf'} onClick={() => setBron('pdf')}>PDF opladen</button>
            </div>
          )}

          {fichebron === 'bouwflow' && (
            <>
              <div className="mb-4">
                <label className="field-label">Project in Bouwflow</label>
                <BouwflowProjectPicker
                  value={bouwflowProject}
                  onChange={(sel) => {
                    setBouwflowProject(sel);
                    if (sel) setMeta((prev) => ({ ...prev, projectNummer: normalizeProjectNummer(sel.projectId), projectTitel: sel.name }));
                  }}
                />
                <p className="field-hint">Zoek op P-nummer of klantnaam. Staat er geen opstartfiche in Bouwflow, laad de fiche dan op als PDF.</p>
              </div>
              <div className="flex justify-end mt-5">
                <button
                  className="btn-primary inline-flex items-center gap-2"
                  disabled={!bouwflowProject || parsing}
                  onClick={() => bouwflowProject && handleParseBouwflow(bouwflowProject.id)}
                >
                  {parsing && <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden />}
                  {parsing ? 'Opstartfiche ophalen…' : 'Opstartfiche ophalen →'}
                </button>
              </div>
            </>
          )}

          {fichebron === 'pdf' && (<>
          <div className="mb-4">
            <label className="block border-2 border-dashed border-accent rounded-lg p-10 text-center bg-accent-50 hover:bg-accent-100 cursor-pointer transition-colors">
              <div className="text-5xl text-accent mb-3">📄</div>
              <div className="font-bold text-lg mb-1">{pdfFile ? `Geselecteerd: ${pdfFile.name}` : 'Klik om opstartfiche te kiezen'}</div>
              <div className="text-sm text-secondary">{pdfFile ? 'Klik om te wijzigen' : 'of sleep een PDF hierheen'}</div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          <details className="mb-4 group">
            <summary className="text-xs text-secondary hover:text-primary cursor-pointer select-none">
              Geavanceerd · ploeg of project vooraf vastleggen
            </summary>
            <div className="mt-4 pt-4 border-t border-border space-y-4">
              {bouwflowEnabled && !manualMode ? (
                <>
                  <div>
                    <label className="field-label">Project (Bouwflow)</label>
                    <BouwflowProjectPicker
                      value={bouwflowProject}
                      onChange={(sel) => {
                        setBouwflowProject(sel);
                        if (sel) {
                          setMeta((prev) => ({
                            ...prev,
                            projectNummer: normalizeProjectNummer(sel.projectId),
                            projectTitel: sel.name,
                          }));
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="field-label">Ploeg (Bouwflow)</label>
                    <BouwflowSubcontractorGroupPicker
                      value={bouwflowGroup}
                      onChange={(sel) => {
                        setBouwflowGroup(sel);
                        if (sel?.localPloeg) setSelectedPloegId(sel.localPloeg.id);
                        else setSelectedPloegId('');
                      }}
                    />
                  </div>
                  <div className="text-right">
                    <button type="button" className="text-xs text-secondary hover:text-primary underline" onClick={() => setManualMode(true)}>
                      Manueel invullen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="field-label">Projectnummer</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="bv. P-1842"
                      value={meta.projectNummer}
                      onChange={(e) => setMeta({ ...meta, projectNummer: normalizeProjectNummer(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Ploeg / onderaannemer</label>
                    <select className="select" value={selectedPloegId} onChange={(e) => setSelectedPloegId(e.target.value)}>
                      <option value="">— AI laat detecteren —</option>
                      {Object.entries(ploegenByFirma).map(([firma, ps]) => (
                        <optgroup key={firma} label={firma}>
                          {ps.map((p) => <option key={p.id} value={p.id}>{p.team}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {bouwflowEnabled && (
                    <div className="text-right">
                      <button type="button" className="text-xs text-secondary hover:text-primary underline" onClick={() => setManualMode(false)}>
                        Bouwflow-picker gebruiken
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </details>

          <div className="flex justify-end mt-5">
            <button
              className="btn-primary inline-flex items-center gap-2"
              disabled={!pdfFile || parsing}
              onClick={handleParse}
            >
              {parsing && (
                <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden />
              )}
              {parsing ? 'AI leest PDF in… (5-15s)' : 'PDF inlezen →'}
            </button>
          </div>
          </>)}
        </div>
      )}

      {/* Volledig-scherm overlay zodat de gebruiker meteen feedback krijgt en niet
          op de knop blijft klikken — voorkomt dubbele uploads en geeft duidelijke
          progressindicatie tijdens de AI-call (5-15s). */}
      {parsing && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg shadow-card px-6 py-5 flex items-center gap-4 pointer-events-auto">
            <span className="inline-block w-6 h-6 border-[3px] border-warmgrey border-t-primary rounded-full animate-spin" aria-hidden />
            <div>
              <div className="font-bold text-primary">{fichebron === 'bouwflow' ? 'Opstartfiche ophalen uit Bouwflow…' : 'AI leest opstartfiche…'}</div>
              <div className="text-xs text-secondary mt-0.5">{fichebron === 'bouwflow' ? 'Posten en hoeveelheden worden opgehaald en gematcht met de prijslijst.' : 'Duurt 5–15 seconden — ondertussen wordt elk werkpost-item geëxtraheerd en gematcht.'}</div>
            </div>
          </div>
        </div>
      )}

      {/* === STAP 2 === */}
      {step === 2 && (
        <>
          {/* Ploeg-fallback banner — zichtbaar zolang er geen ploeg is gekozen.
              De AI heeft niets herkend, dus we vragen de gebruiker handmatig
              een ploeg te kiezen. Bij keuze halen we de prijslijst op en
              draaien matching client-side (geen tweede AI-call). */}
          {!selectedPloegId && parseResult && (
            <div className="card mb-5 border-l-4 border-warning">
              <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
                <span className="text-warning">!</span> Ploeg niet automatisch gedetecteerd
              </h3>
              <div className="text-sm text-secondary mb-3 space-y-1">
                {parseResult.detected?.bouwflowSheetGroupName && parseResult.detected.bouwflowSheetGroupId ? (
                  <>
                    <p>
                      Bouwflow toont <strong>"{parseResult.detected.bouwflowSheetGroupName}"</strong> (id <strong>{parseResult.detected.bouwflowSheetGroupId}</strong>) als ploeg op deze opstartfiche. Koppel 'm in één klik aan een lokale ploeg:
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <select
                        className="select max-w-xs"
                        defaultValue=""
                        onChange={async (e) => {
                          const ploegId = e.target.value;
                          if (!ploegId || !parseResult) return;
                          const det = parseResult.detected!;
                          const targetPloeg = ploegen.find((p) => p.id === ploegId);
                          if (!targetPloeg) return;
                          try {
                            await api.ploegen.update(ploegId, { bouwflowId: det.bouwflowSheetGroupId });
                            await ploegenQ.refetch();
                            const { pricelist: pl, matched } = await matchWithLearning(targetPloeg.pricelistId, parseResult.items);
                            setMatchedLines(matched.matchedLines);
                            setMeerwerkLines(matched.meerwerkLines);
                            setSkippedItems(matched.skippedItems);
                            setGevel(null);
                            setSelectedPloegId(ploegId);
                            setParseResult((prev) => prev ? {
                              ...prev,
                              matching: matched,
                              ploeg: { id: targetPloeg.id, team: targetPloeg.team, firma: targetPloeg.firma },
                              pricelistId: pl.id,
                            } : prev);
                            toast.success(`Bouwflow #${det.bouwflowSheetGroupId} gekoppeld aan ${targetPloeg.team} — matching uitgevoerd`);
                          } catch (err) {
                            toast.error('Koppelen mislukt: ' + (err instanceof Error ? err.message : 'fout'));
                          }
                        }}
                      >
                        <option value="">— Koppel #{parseResult.detected.bouwflowSheetGroupId} aan lokale ploeg —</option>
                        {Object.entries(ploegenByFirma).map(([firma, ps]) => (
                          <optgroup key={firma} label={firma}>
                            {ps.map((p) => <option key={p.id} value={p.id}>{p.team}</option>)}
                          </optgroup>
                        ))}
                      </select>
                      <span className="text-xs">of <Link to="/ploegen" className="underline">beheer alle koppelingen</Link></span>
                    </div>
                  </>
                ) : parseResult.detected?.extractedTeamNaam ? (
                  <p>De AI vond <strong>"{parseResult.detected.extractedTeamNaam}"</strong> op de fiche, maar dat matcht geen lokale ploeg.</p>
                ) : (
                  <>
                    <p>Geen ploeg-info gevonden:</p>
                    <ul className="list-disc list-inside text-xs ml-1">
                      <li>De PDF zelf vermeldt geen teamnaam</li>
                      {parseResult.detected?.bouwflowProject ? (
                        <li>Bouwflow-project <strong>{parseResult.detected.bouwflowProject.projectId}</strong> ({parseResult.detected.bouwflowProject.name}) gevonden, maar de bijhorende opstartfiche heeft geen ploeg toegewezen — voeg in Bouwflow een subcontractor-group toe aan de opstartfiche</li>
                      ) : parseResult.detected?.extractedProjectNummer ? (
                        <li>Projectnummer <strong>{parseResult.detected.extractedProjectNummer}</strong> uit PDF, maar niet gevonden in Bouwflow</li>
                      ) : (
                        <li>Geen Bouwflow-project gevonden (geen projectnummer of titel-match)</li>
                      )}
                    </ul>
                  </>
                )}
                <p className="font-semibold pt-1">Kies handmatig:</p>
              </div>
              <select
                className="select max-w-md"
                value={selectedPloegId}
                onChange={async (e) => {
                  const newId = e.target.value;
                  setSelectedPloegId(newId);
                  if (!newId || !parseResult) return;
                  try {
                    const { pricelist: pl, matched } = await matchWithLearning(
                      ploegen.find((p) => p.id === newId)!.pricelistId,
                      parseResult.items,
                    );
                    setMatchedLines(matched.matchedLines);
                    setMeerwerkLines(matched.meerwerkLines);
                    setSkippedItems(matched.skippedItems);
                    setGevel(null);
                    // Update parseResult.matching zodat stap 3 de reviewItems/unmatchedItems
                    // van DEZE pricelist te zien krijgt (anders blijft de queue leeg).
                    setParseResult((prev) => prev ? {
                      ...prev,
                      matching: matched,
                      ploeg: { id: newId, team: ploegen.find((p) => p.id === newId)!.team, firma: ploegen.find((p) => p.id === newId)!.firma },
                      pricelistId: pl.id,
                    } : prev);
                    toast.success(`Matching uitgevoerd voor ${pl.name}`);
                  } catch (err) {
                    toast.error('Prijslijst laden mislukt: ' + (err instanceof Error ? err.message : 'fout'));
                  }
                }}
              >
                <option value="">— Kies ploeg —</option>
                {Object.entries(ploegenByFirma).map(([firma, ps]) => (
                  <optgroup key={firma} label={firma}>
                    {ps.map((p) => <option key={p.id} value={p.id}>{p.team}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          <div className="card mb-5">
            <h3 className="text-lg font-bold mb-3">Projectgegevens</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="field-label">Projectnummer</label>
                <input className="input" placeholder="bv. P-1842" value={meta.projectNummer} onChange={(e) => setMeta({ ...meta, projectNummer: normalizeProjectNummer(e.target.value) })} />
              </div>
              <div>
                <label className="field-label">Klant</label>
                <input className="input" value={meta.klantNaam} onChange={(e) => setMeta({ ...meta, klantNaam: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Adres</label>
                <input className="input" value={meta.klantAdres} onChange={(e) => setMeta({ ...meta, klantAdres: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Adviseur</label>
                <input className="input" value={meta.adviseur} onChange={(e) => setMeta({ ...meta, adviseur: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Projecttitel</label>
                <input className="input" value={meta.projectTitel} onChange={(e) => setMeta({ ...meta, projectTitel: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Opmerkingen / extra info uit de opstartfiche — zichtbaar voor handmatige check.
              De AI extraheert deze tekst, maar ze is editable zodat de calculator kan aanvullen. */}
          <div className="card mb-5 border-l-4 border-warning">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <span className="text-warning">⚠</span> Opmerkingen / instructies uit de opstartfiche
              </h3>
              <span className="text-xs text-secondary normal-case tracking-normal">
                Lees aandachtig — vaak staan hier prijsregelingen of speciale voorwaarden
              </span>
            </div>
            <textarea
              className="textarea w-full font-normal normal-case tracking-normal"
              rows={meta.opmerkingen.split('\n').length + 1}
              value={meta.opmerkingen}
              placeholder="Geen extra opmerkingen op deze fiche gevonden. Voeg hier handmatig zaken toe die de calculator moet weten."
              onChange={(e) => setMeta({ ...meta, opmerkingen: e.target.value })}
            />
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-bold">Geparseerde items ({editedItems.length})</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-secondary">Pas hoeveelheden aan, of voeg een ontbrekend item toe</span>
                <button className="btn-ghost btn-sm" onClick={addManualItem} title="Voeg een ontbrekend item toe dat de AI gemist heeft">+ Item handmatig toevoegen</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="thead-row">
                    <th className="text-left px-3 py-2">Beschrijving</th>
                    <th className="text-right px-3 py-2 w-28">Geadviseerd</th>
                    <th className="text-right px-3 py-2 w-28">Werkelijk</th>
                    <th className="text-left px-3 py-2 w-20">Eenheid</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...itemsByCategorie.entries()].flatMap(([cat, items]) => [
                    <tr key={`cat-${cat}`} className="bg-primary-50">
                      <td colSpan={5} className="text-xs font-bold px-3 py-2">{cat}</td>
                    </tr>,
                    ...items.map((it) => (
                      <tr key={it.id} id={`parsed-item-${it.id}`} className="border-b border-lightgrey">
                        <td className="px-3 py-1">
                          <input className="w-full bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.werken} onChange={(e) => updateItem(it.id, 'werken', e.target.value)} />
                        </td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="0.01" className="w-full text-right bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.geadviseerdeHoeveelheid} onChange={(e) => updateItem(it.id, 'geadviseerdeHoeveelheid', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="0.01" className="w-full text-right bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.werkelijkeHoeveelheid} onChange={(e) => updateItem(it.id, 'werkelijkeHoeveelheid', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td className="px-3 py-1">
                          <input className="w-full bg-transparent text-sm py-1 uppercase hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm" value={it.eenheid} onChange={(e) => updateItem(it.id, 'eenheid', e.target.value.toUpperCase())} />
                        </td>
                        <td className="px-3 py-1">
                          <button className="btn-ghost btn-sm" onClick={() => removeItem(it.id)} title="Verwijder">✕</button>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
              <div ref={tableEndRef} />
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t border-border mt-6 -mx-6 px-5 py-4 flex justify-between flex-wrap gap-4 items-center">
            <button className="btn-ghost" onClick={() => setStep(1)}>← Terug</button>
            <div className="flex items-center gap-3">
              {!selectedPloegId && (
                <span className="text-xs text-warning normal-case tracking-normal">
                  Kies eerst een ploeg om door te gaan
                </span>
              )}
              <button
                className="btn-primary"
                onClick={() => setStep(3)}
                disabled={!selectedPloegId}
                title={!selectedPloegId ? 'Geen ploeg geselecteerd — kies een ploeg uit de banner bovenaan' : ''}
              >
                Volgende: matching →
              </button>
            </div>
          </div>
        </>
      )}

      {/* === STAP 3 === */}
      {step === 3 && parseResult && pricelist && (
        <Step3
          matchedLines={matchedLines}
          setMatchedLines={setMatchedLines}
          meerwerkLines={meerwerkLines}
          setMeerwerkLines={setMeerwerkLines}
          skippedItems={skippedItems}
          setSkippedItems={setSkippedItems}
          extraRegels={extraRegels}
          setExtraRegels={setExtraRegels}
          gevel={gevel}
          isGevel={isGevel}
          gevelStandaard={gevelStandaard}
          onGevelModal={setGevelModal}
          projectNummer={meta.projectNummer}
          ploegId={selectedPloegId}
          ploegen={ploegen}
          magBevestigen={magBevestigen}
          toonFouten={toonFouten}
          live={live}
          afwijkingen={afwijkingen}
          onReden={(a, reden) => {
            if (a.soort === 'werkpost') setMatchedLines((prev) => zetReden(prev, a.lijnId, reden));
            else setMeerwerkLines((prev) => zetReden(prev, a.lijnId, reden));
          }}
          reviewItems={parseResult.matching.reviewItems}
          unmatchedItems={parseResult.matching.unmatchedItems}
          editedItems={editedItems}
          pricelist={pricelist}
          totals={totals}
          onBack={() => setStep(2)}
          onSaveDraft={() => handleSave('draft')}
          onSaveApproved={() => handleSave('approved')}
          vragenStaat={vragenStaat}
          onVragen={() => setVragenModal(true)}
          onderaannemerVerschil={parseResult.detected?.onderaannemerVerschil ?? null}
        />
      )}
      {step === 3 && vragenModal && !gevelModal && (
        <BevestigVragenModal
          staat={vragenStaat}
          magBevestigen={magBevestigen}
          onToepassen={(n) => {
            setMatchedLines(n.matchedLines);
            setMeerwerkLines(n.meerwerkLines);
            setExtraOffertes(n.extraOffertes);
            setExtraInfoVragen(n.extraInfoVragen);
            setExtraInfoGenegeerd(n.genegeerd);
            setVragenModal(false);
          }}
          onSluit={() => setVragenModal(false)}
        />
      )}
      {step === 3 && gevelModal && selectedPloegId && (
        <GevelVariantModal
          ploegId={selectedPloegId}
          standaardVariant={gevelStandaard}
          huidigeVariant={gevel?.variant}
          magBevestigen={magBevestigen}
          verversen={gevelModal === 'verversen'}
          onBevestig={(cfg) => { setGevel(cfg); setGevelModal(null); }}
          onSluit={() => setGevelModal(null)}
        />
      )}
      {step === 3 && !pricelist && (
        <div className="card max-w-3xl flex items-center gap-4">
          {pricelistDetailQ.isError ? (
            <>
              <span className="text-danger text-2xl">!</span>
              <div className="flex-1">
                <div className="font-bold">Prijslijst kon niet geladen worden</div>
                <div className="text-sm text-secondary">{(pricelistDetailQ.error as Error)?.message ?? 'Onbekende fout'}. Ga terug en kies opnieuw.</div>
              </div>
              <button className="btn-ghost" onClick={() => setStep(2)}>← Terug</button>
            </>
          ) : !ploeg ? (
            <>
              <span className="text-warning text-2xl">!</span>
              <div className="flex-1">
                <div className="font-bold">Geen ploeg geselecteerd</div>
                <div className="text-sm text-secondary">Ga terug naar stap 2 en kies eerst een ploeg.</div>
              </div>
              <button className="btn-ghost" onClick={() => setStep(2)}>← Terug</button>
            </>
          ) : (
            <>
              <span className="inline-block w-5 h-5 border-[3px] border-warmgrey border-t-primary rounded-full animate-spin" aria-hidden />
              <div>
                <div className="font-bold">Prijslijst laden…</div>
                <div className="text-xs text-secondary">Even geduld — een momentje na een ploeg-wissel.</div>
              </div>
            </>
          )}
        </div>
      )}
    </AppShell>
  );
}

interface Step3Props {
  matchedLines: MatchedLine[];
  setMatchedLines: React.Dispatch<React.SetStateAction<MatchedLine[]>>;
  meerwerkLines: MeerwerkLine[];
  setMeerwerkLines: React.Dispatch<React.SetStateAction<MeerwerkLine[]>>;
  skippedItems: SkippedItem[];
  setSkippedItems: React.Dispatch<React.SetStateAction<SkippedItem[]>>;
  extraRegels: ExtraRegel[];
  setExtraRegels: React.Dispatch<React.SetStateAction<ExtraRegel[]>>;
  gevel: GevelConfig | null;
  isGevel: boolean;
  gevelStandaard: GevelVariant;
  onGevelModal: (m: 'kies' | 'verversen') => void;
  projectNummer: string;
  ploegId: string;
  ploegen: PloegMetPrijslijsten[];
  magBevestigen: boolean;
  toonFouten: boolean;
  live: ReturnType<typeof useLivePercentages>;
  /** Prijsafwijkingen t.o.v. de standaardprijs (27okt 2). */
  afwijkingen: LiveAfwijking[];
  onReden: (a: LiveAfwijking, reden: string) => void;
  reviewItems: MatchResult['reviewItems'];
  unmatchedItems: MatchResult['unmatchedItems'];
  /** Volledige editedItems-lijst zodat handmatig in stap 2 toegevoegde items de pending-queue kunnen vullen. */
  editedItems: ParsedItem[];
  pricelist: Pricelist;
  totals: TotalsMetGevel;
  onBack: () => void;
  onSaveDraft: () => void;
  onSaveApproved: () => void;
  vragenStaat: VragenStaat;
  onVragen: () => void;
  onderaannemerVerschil: { fiche: string; bouwflow: string } | null;
}

type PendingItem = { parsedItem: ParsedItem; suggestions: { item: PricelistItem; score: number }[]; kind: 'review' | 'unmatched' };

function Step3(props: Step3Props) {
  const { matchedLines, setMatchedLines, meerwerkLines, setMeerwerkLines, skippedItems, setSkippedItems, extraRegels, setExtraRegels, gevel, isGevel, gevelStandaard, onGevelModal, projectNummer, ploegId, ploegen, magBevestigen, toonFouten, live, afwijkingen, onReden, reviewItems, unmatchedItems, editedItems, pricelist, totals, onBack, onSaveDraft, onSaveApproved, vragenStaat, onVragen, onderaannemerVerschil } = props;
  const vervangenIds = new Set(totals.gevel?.vervangenLijnIds ?? []);
  const openOverlap = matchedLines.filter((l) => l.overlap?.status === 'open').length;
  const openVragen = telVragen(vragenStaat);
  const blokkeerGoedkeuren = isGevel && !gevel?.bevestigd
    ? 'Bevestig eerst welke gevelprijslijst (A of B) geldt.'
    : openOverlap > 0 ? `Beslis eerst over ${openOverlap} post(en) die normaal al inbegrepen zijn.`
      : openVragen.offertes > 0 ? 'Bevestig eerst de extra werken uit offertes na de opstartfiche.'
        : openVragen.eenheden > 0 ? 'Bevestig eerst de posten met een andere eenheid dan de prijslijst.' : null;

  const [pending, setPending] = useState<PendingItem[]>(() => {
    const list: PendingItem[] = [
      ...reviewItems.map<PendingItem>((r) => ({ parsedItem: r.parsedItem, suggestions: r.suggestions, kind: 'review' })),
      ...unmatchedItems.map<PendingItem>((u) => ({ parsedItem: u.parsedItem, suggestions: u.suggestions, kind: 'unmatched' })),
    ];
    // Handmatig in stap 2 toegevoegde items (id-prefix `pi_manual_`) zijn nooit door de
    // matcher gegaan — zet ze in de pending-queue zodat de gebruiker ze kan koppelen.
    const seen = new Set(list.map((p) => p.parsedItem.id));
    for (const it of editedItems) {
      if (it.id.startsWith('pi_manual_') && !seen.has(it.id)) {
        list.push({ parsedItem: it, suggestions: [], kind: 'unmatched' });
      }
    }
    return list;
  });

  const relevantPlItems = useMemo(
    () => (pricelist.items ?? []).filter((it) => !it.isNvt && it.naamArbeid !== 'NVT'),
    [pricelist.items],
  );


  /** Pas hoeveelheid of prijs aan op een sub-item en hercompute alle aggregaten op de regel. */
  const updateSubItem = (
    lineId: string,
    parsedItemId: string,
    patch: { hoeveelheid?: number; prijs?: number },
  ) => {
    setMatchedLines((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      const newSubs = l.subItems.map((s) => {
        if (s.parsedItemId !== parsedItemId) return s;
        const hv = patch.hoeveelheid ?? s.hoeveelheid;
        const pr = patch.prijs ?? s.prijs;
        return { ...s, hoeveelheid: hv, prijs: pr, subtotaal: hv * pr };
      });
      const totHv = newSubs.reduce((acc, s) => acc + s.hoeveelheid, 0);
      const totSt = newSubs.reduce((acc, s) => acc + s.subtotaal, 0);
      const firstPrice = newSubs[0]?.prijs ?? 0;
      const allSame = newSubs.every((s) => s.prijs === firstPrice);
      return { ...l, hoeveelheid: totHv, subtotaal: totSt, prijs: allSame ? firstPrice : null, subItems: newSubs };
    }));
  };

  const updateMwPrijs = (id: string, prijs: number) => {
    setMeerwerkLines((prev) => prev.map((m) => m.id === id ? { ...m, prijs, subtotaal: prijs * m.hoeveelheid } : m));
  };
  const updateMwQty = (id: string, qty: number) => {
    setMeerwerkLines((prev) => prev.map((m) => m.id === id ? { ...m, hoeveelheid: qty, subtotaal: m.prijs * qty } : m));
  };
  const removeMw = (id: string) => setMeerwerkLines((prev) => prev.filter((m) => m.id !== id));
  const removeLine = (id: string) => setMatchedLines((prev) => prev.filter((l) => l.id !== id));

  /** Match een pending item handmatig met een prijslijst-item. */
  const resolvePendingToMatch = (parsedItemId: string, plItemId: string) => {
    const pi = pending.find((p) => p.parsedItem.id === parsedItemId);
    const plItem = relevantPlItems.find((it) => it.id === plItemId);
    if (!pi || !plItem) return;
    const sub: MatchedSubItem = {
      parsedItemId: pi.parsedItem.id,
      pdfNaam: pi.parsedItem.werken,
      plItemId: plItem.id,
      hoeveelheid: pi.parsedItem.werkelijkeHoeveelheid || 0,
      eenheid: plItem.eenheid,
      prijs: plItem.prijs,
      subtotaal: (pi.parsedItem.werkelijkeHoeveelheid || 0) * plItem.prijs,
      confidence: 1.0,
      autoMatched: false,
      matchSource: 'manual',
    };
    setMatchedLines((prev) => {
      const existing = prev.find((l) => l.naamArbeid === plItem.naamArbeid);
      if (existing) {
        const newSubs = [...existing.subItems, sub];
        const totHv = newSubs.reduce((s, x) => s + x.hoeveelheid, 0);
        const totSt = newSubs.reduce((s, x) => s + x.subtotaal, 0);
        const allSamePrice = newSubs.every((x) => x.prijs === existing.prijs);
        return prev.map((l) => l.id === existing.id ? {
          ...l,
          hoeveelheid: totHv,
          subtotaal: totSt,
          prijs: allSamePrice ? existing.prijs : null,
          consumedParsedItemIds: [...l.consumedParsedItemIds, pi.parsedItem.id],
          subItems: newSubs,
        } : l);
      }
      return [...prev, {
        id: 'ml_' + Math.random().toString(36).slice(2, 10),
        naamArbeid: plItem.naamArbeid,
        eenheid: plItem.eenheid,
        prijs: plItem.prijs,
        hoeveelheid: sub.hoeveelheid,
        subtotaal: sub.subtotaal,
        consumedParsedItemIds: [pi.parsedItem.id],
        autoMatched: false,
        subItems: [sub],
      }];
    });
    setPending((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  /** Pending item naar meerwerk converteren. */
  const resolvePendingToMeerwerk = (parsedItemId: string, prijs: number) => {
    const pi = pending.find((p) => p.parsedItem.id === parsedItemId);
    if (!pi) return;
    const hv = pi.parsedItem.werkelijkeHoeveelheid || 0;
    setMeerwerkLines((prev) => [...prev, {
      id: 'mw_' + Math.random().toString(36).slice(2, 10),
      pdfNaam: pi.parsedItem.werken,
      naamArbeid: 'MEERWERK',
      categorie: pi.parsedItem.categorie,
      eenheid: pi.parsedItem.eenheid || '',
      hoeveelheid: hv,
      prijs,
      subtotaal: prijs * hv,
      parsedItemId: pi.parsedItem.id,
    }]);
    setPending((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  /** Pending item negeren. Komt bij de overgeslagen items terecht mét de
   *  gebruikers-reden, zodat de server 'm onthoudt voor volgende fiches. */
  const skipPending = (parsedItemId: string) => {
    const pi = pending.find((p) => p.parsedItem.id === parsedItemId);
    if (pi) {
      setSkippedItems((prev) => [
        ...prev.filter((s) => s.parsedItemId !== parsedItemId),
        { parsedItemId, pdfNaam: pi.parsedItem.werken, reden: USER_SKIP_REDEN },
      ]);
    }
    setPending((prev) => prev.filter((p) => p.parsedItem.id !== parsedItemId));
  };

  /** Voeg handmatig een pricelist-werkpost toe (zonder PDF-regel). Krijgt een sub-item met de
   *  koppeling naar de prijslijstpost, zodat schijven en overlapcontrole ook hier werken. */
  const addManualMatch = (plItemId: string, hoeveelheid: number) => {
    const plItem = relevantPlItems.find((it) => it.id === plItemId);
    if (!plItem || hoeveelheid <= 0) return;
    const sub: MatchedSubItem = {
      parsedItemId: 'pi-manual-' + Math.random().toString(36).slice(2, 10),
      pdfNaam: plItem.pdfNaam + ' (handmatig toegevoegd)',
      plItemId: plItem.id,
      hoeveelheid,
      eenheid: plItem.eenheid,
      prijs: plItem.prijs,
      subtotaal: plItem.prijs * hoeveelheid,
      confidence: 1,
      autoMatched: false,
      matchSource: 'manual-add',
    };
    setMatchedLines((prev) => {
      const existing = prev.find((l) => l.naamArbeid === plItem.naamArbeid);
      if (existing) {
        const subs = [...existing.subItems, sub];
        const allSame = subs.every((x) => x.prijs === subs[0]!.prijs);
        return prev.map((l) => l.id === existing.id ? {
          ...l,
          subItems: subs,
          hoeveelheid: subs.reduce((acc, x) => acc + x.hoeveelheid, 0),
          subtotaal: subs.reduce((acc, x) => acc + x.subtotaal, 0),
          prijs: allSame ? subs[0]!.prijs : null,
        } : l);
      }
      return [...prev, {
        id: 'ml_' + Math.random().toString(36).slice(2, 10),
        naamArbeid: plItem.naamArbeid,
        eenheid: plItem.eenheid,
        prijs: plItem.prijs,
        hoeveelheid,
        subtotaal: plItem.prijs * hoeveelheid,
        consumedParsedItemIds: [],
        autoMatched: false,
        subItems: [sub],
        ...(plItem.schijven ? { schijven: plItem.schijven } : {}),
      }];
    });
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="chip-success">{matchedLines.length} gematcht</span>
        {meerwerkLines.length > 0 && <span className="chip-accent">{meerwerkLines.length} meerwerk</span>}
        {pending.length > 0 && <span className="chip-warning">{pending.length} te reviewen</span>}
        {skippedItems.length > 0 && <span className="chip-neutral">{skippedItems.length} overgeslagen</span>}
      </div>

      {pending.length > 0 && (
        <>
          <h3 className="font-bold mb-3 text-warning">⚠ Te reviewen</h3>
          <p className="text-secondary text-sm mb-3">Items die de AI niet automatisch kon matchen — kies een arbeid uit de prijslijst, maak er meerwerk van, of negeer.</p>
          <div className="space-y-2 mb-5">
            {pending.map((p) => (
              <PendingRow
                key={p.parsedItem.id}
                pending={p}
                pricelistItems={relevantPlItems}
                onMatch={(plItemId) => resolvePendingToMatch(p.parsedItem.id, plItemId)}
                onMeerwerk={(prijs) => resolvePendingToMeerwerk(p.parsedItem.id, prijs)}
                onSkip={() => skipPending(p.parsedItem.id)}
              />
            ))}
          </div>
        </>
      )}

      {onderaannemerVerschil && (
        <div className="card mb-5 border-l-4 border-warning normal-case tracking-normal" role="status">
          <strong>Onderaannemer wijkt af van Bouwflow.</strong> Op de opstartfiche staat <strong>{onderaannemerVerschil.fiche}</strong>, in Bouwflow <strong>{onderaannemerVerschil.bouwflow}</strong>.
          De naam op de opstartfiche geldt; het verschil komt in de log. Kies hierboven een andere ploeg als dat niet klopt.
        </div>
      )}
      <BevestigVragenKaart staat={vragenStaat} onOpen={onVragen} />
      <OverlapVragen lines={matchedLines} editable magBevestigen={magBevestigen} onBeslis={(lineId, status) => setMatchedLines((prev) => beslisOverlap(prev, lineId, status))} />

      <h3 className="font-bold mb-3 text-success">✓ Gematcht</h3>
      {matchedLines.length === 0 ? (
        <p className="text-secondary mb-4">Geen items gematcht.</p>
      ) : matchedLines.map((line) => (
        <div key={line.id} className="bg-white rounded-md mb-3 overflow-hidden shadow-xs">
          <div className="flex items-center gap-3 px-4 py-3 bg-success-100 border-l-4 border-success">
            <span className="flex-1 font-semibold">
              {line.naamArbeid}
              {vervangenIds.has(line.id) && <span className="chip-neutral ml-2" title="Geprijsd door de crepi-ladder">via gevelladder</span>}
              {line.overlap && <span className={`ml-2 ${line.overlap.status === 'bevestigd' ? 'chip-success' : line.overlap.status === 'open' ? 'chip-warning' : 'chip-neutral'}`}>{line.overlap.status === 'open' ? 'te bevestigen' : line.overlap.status === 'bevestigd' ? 'apart betaald' : 'niet betaald'}</span>}
              {line.schijven && <span className="chip-neutral ml-2">schijven</span>}
            </span>
            <span className="font-bold tabular-nums">
              {line.schijven ? <em className="text-secondary">schijven</em> : line.prijs == null ? <em className="text-secondary">prijs varieert</em> : `${fmtEUR(line.prijs)}/${line.eenheid}`}
              {' · '}<strong className={vervangenIds.has(line.id) || (line.overlap && line.overlap.status !== 'bevestigd') ? 'line-through text-secondary' : ''}>{fmtEUR(vervangenIds.has(line.id) ? line.subtotaal : lineSubtotaal(line))}</strong>
              <span className="text-secondary ml-2">({fmtNum(line.hoeveelheid)} {line.eenheid})</span>
            </span>
            <button className="btn-ghost btn-sm" onClick={() => removeLine(line.id)}>✕</button>
          </div>
          {line.subItems.length > 0 && (
            <div className="px-5 py-3">
              <table className="w-full">
                <thead>
                  <tr className="thead-row">
                    <th className="text-left">PDF-naam</th>
                    <th className="text-right w-28">Hoeveelheid</th>
                    <th className="text-right w-28">Prijs (€)</th>
                    <th className="text-right w-28">Subtotaal</th>
                  </tr>
                </thead>
                <tbody>
                  {line.subItems.map((s) => (
                    <tr key={s.parsedItemId} className="border-t border-lightgrey">
                      <td className="text-sm py-1">{s.pdfNaam}{s.confidence > 0 && s.confidence < 1 && <span className="text-secondary text-xs ml-2">({(s.confidence * 100).toFixed(0)}% fuzzy)</span>}{s.matchSource === 'learned' && <span className="chip-neutral ml-2" title="Uit het geheugen: eerder handmatig zo gematcht">geleerd</span>}</td>
                      <td className="text-right py-1">
                        <input
                          type="number"
                          step="0.01"
                          value={s.hoeveelheid}
                          onChange={(e) => updateSubItem(line.id, s.parsedItemId, { hoeveelheid: parseFloat(e.target.value) || 0 })}
                          className="border border-border rounded px-1 py-0.5 w-20 text-right tabular-nums text-sm"
                        />
                        <span className="text-xs text-secondary ml-1">{s.eenheid}</span>
                      </td>
                      <td className="text-right py-1">
                        <input
                          type="number"
                          step="0.01"
                          value={s.prijs}
                          onChange={(e) => updateSubItem(line.id, s.parsedItemId, { prijs: parseFloat(e.target.value) || 0 })}
                          className="border border-border rounded px-1 py-0.5 w-24 text-right tabular-nums text-sm"
                        />
                      </td>
                      <td className="text-right text-sm tabular-nums font-semibold py-1">{fmtEUR(s.subtotaal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Handmatig pricelist-werkpost toevoegen — bv. arbeid die niet op de PDF stond. */}
      <ManualAddRow
        pricelistItems={relevantPlItems}
        onAdd={addManualMatch}
        onAddFree={(line) => setMatchedLines((prev) => [...prev, line])}
      />

      {meerwerkLines.length > 0 && (
        <>
          <h3 className="font-bold mt-5 mb-3 text-accent">Meerwerk</h3>
          {meerwerkLines.map((line) => (
            <div key={line.id} className="bg-white rounded-md mb-2 shadow-xs">
              <div className="flex items-center gap-3 px-4 py-3 bg-accent-50 border-l-4 border-accent flex-wrap">
                <span className="flex-1 font-semibold">
                  {line.pdfNaam}
                  {line.extraOfferte && (
                    <span className={`ml-2 ${line.extraOfferte.status === 'open' ? 'chip-warning' : 'chip-neutral'}`} title={`Extra werk na de opstartfiche, offerte ${line.extraOfferte.nummer} (SPEC_workflow 4)`}>
                      {line.extraOfferte.status === 'open' ? 'extra offerte, te bevestigen' : `offerte ${line.extraOfferte.nummer}`}
                    </span>
                  )}
                  {line.extraInformatie && <span className="chip-neutral ml-2" title={`Uit extra informatie: “${line.extraInformatie.fragment}”`}>uit extra informatie</span>}
                </span>
                <span className="flex items-center gap-2 text-sm">
                  <input type="number" step="0.01" value={line.prijs} onChange={(e) => updateMwPrijs(line.id, parseFloat(e.target.value) || 0)} className="border border-border rounded px-2 py-0.5 w-20 text-right tabular-nums" placeholder="prijs" /> €
                  ×
                  <input type="number" step="0.01" value={line.hoeveelheid} onChange={(e) => updateMwQty(line.id, parseFloat(e.target.value) || 0)} className="border border-border rounded px-2 py-0.5 w-20 text-right tabular-nums" />
                  {line.eenheid}
                  = <strong>{fmtEUR(line.subtotaal)}</strong>
                </span>
                <button className="btn-ghost btn-sm" onClick={() => removeMw(line.id)}>✕</button>
              </div>
            </div>
          ))}
        </>
      )}

      {skippedItems.length > 0 && (
        <>
          <h3 className="font-bold mt-5 mb-3 text-secondary">Overgeslagen items</h3>
          <div className="card-flat">
            {skippedItems.map((s, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-b border-lightgrey last:border-b-0">
                <span>{s.pdfNaam}</span>
                <span className="text-secondary">{s.reden}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Prijsafwijkingen t.o.v. standaardprijzen (27okt 2) — direct onder de overgeslagen items. */}
      <div className="mt-6" />
      <PrijsafwijkingenBlok afwijkingen={afwijkingen} calculatieTotaal={totals.totaal} editing toonFouten={toonFouten} onReden={onReden} />

      <GevelBlock
        gevel={isGevel ? gevel : null}
        resultaat={totals.gevel}
        isGevel={isGevel}
        standaardVariant={gevelStandaard}
        editable
        onKies={() => onGevelModal('kies')}
        onVerversen={() => onGevelModal('verversen')}
      />

      {/* Extra werken (nieuw) en aanrekenbare kosten aan onderaannemer — SPEC_wijzigingen 3 en 4 */}
      <ExtraRegelsSection soort="extra_werk" regels={extraRegels} onChange={setExtraRegels} editing projectNummer={projectNummer} ploegId={ploegId} ploegen={ploegen} magBevestigen={magBevestigen} toonFouten={toonFouten} />
      <ExtraRegelsSection soort="aanrekenbare_kost" regels={extraRegels} onChange={setExtraRegels} editing projectNummer={projectNummer} ploegId={ploegId} ploegen={ploegen} magBevestigen={magBevestigen} toonFouten={toonFouten} />
      <GelinkteProjectenEffect effecten={live.gelinkt} />

      <div className="sticky bottom-0 bg-white border-t border-border mt-6 -mx-6 px-5 py-4 flex justify-between items-center flex-wrap gap-4">
        <div className="flex gap-6 items-baseline flex-wrap">
          <div>
            <div className="text-xs font-medium text-secondary">Subtotaal</div>
            <div className="text-lg font-bold">{fmtEUR(totals.subtotaal)}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-secondary">Meerwerk</div>
            <div className="text-lg font-bold">{fmtEUR(totals.meerwerk)}</div>
          </div>
          {totals.extraWerk > 0 && (
            <div>
              <div className="text-xs font-medium text-secondary">Extra werken</div>
              <div className="text-lg font-bold">{fmtEUR(totals.extraWerk)}</div>
            </div>
          )}
          {totals.aanrekening > 0 && (
            <div>
              <div className="text-xs font-medium text-danger">Aanrekenbare kosten</div>
              <div className="text-lg font-bold text-danger">−{fmtEUR(totals.aanrekening)}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-medium text-secondary">Netto te betalen</div>
            <div className="text-2xl font-extrabold">{fmtEUR(totals.totaal)}</div>
          </div>
          <div title={live.eigen.noemer != null ? `t.o.v. ${live.eigen.basis} ${fmtEUR(live.eigen.noemer)}` : 'Geen referentie bekend'}>
            <div className="text-xs font-medium text-secondary">% Project</div>
            <div className="text-lg font-bold"><PctWaarde pct={live.eigen.naar} voorlopig={live.eigen.voorlopig} /></div>
          </div>
        </div>
        <div className="flex gap-3">
          <button className="btn-ghost" onClick={onBack}>← Terug</button>
          <button className="btn-ghost" onClick={onSaveDraft}>Opslaan als concept</button>
          <button className="btn-primary" onClick={onSaveApproved} disabled={!!blokkeerGoedkeuren} title={blokkeerGoedkeuren ?? undefined}>Goedkeuren</button>
        </div>
      </div>
    </>
  );
}

interface PendingRowProps {
  pending: PendingItem;
  pricelistItems: PricelistItem[];
  onMatch: (plItemId: string) => void;
  onMeerwerk: (prijs: number) => void;
  onSkip: () => void;
}

function PendingRow({ pending: p, pricelistItems, onMatch, onMeerwerk, onSkip }: PendingRowProps) {
  const [mode, setMode] = useState<'pick' | 'meerwerk'>('pick');
  const [plItemId, setPlItemId] = useState(p.suggestions[0]?.item.id ?? '');
  const [mwPrijs, setMwPrijs] = useState(0);

  return (
    <div className="bg-white rounded-md shadow-xs">
      <div className="px-4 py-2 bg-warning-100 border-l-4 border-warning flex items-center gap-3 flex-wrap">
        <span className="flex-1 font-semibold text-sm">{p.parsedItem.werken}</span>
        <span className="text-xs text-secondary">{fmtNum(p.parsedItem.werkelijkeHoeveelheid)} {p.parsedItem.eenheid}</span>
        {p.kind === 'review' && p.suggestions[0] && (
          <span className="text-xs text-secondary">Beste suggestie: {(p.suggestions[0].score * 100).toFixed(0)}%</span>
        )}
      </div>
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 text-xs">
          <button className={`px-2 py-1 rounded ${mode === 'pick' ? 'bg-primary text-white' : 'bg-warmgrey'}`} onClick={() => setMode('pick')}>Match arbeid</button>
          <button className={`px-2 py-1 rounded ${mode === 'meerwerk' ? 'bg-accent text-white' : 'bg-warmgrey'}`} onClick={() => setMode('meerwerk')}>→ Meerwerk</button>
        </div>
        {mode === 'pick' ? (
          <>
            <div className="flex-1 min-w-[260px]">
              <PricelistItemPicker
                items={pricelistItems}
                suggestions={p.suggestions}
                value={plItemId}
                onChange={setPlItemId}
                placeholder="Zoek arbeid…"
                size="sm"
              />
            </div>
            <button className="btn-primary btn-sm" disabled={!plItemId} onClick={() => onMatch(plItemId)}>✓ Match</button>
          </>
        ) : (
          <>
            <span className="text-xs text-secondary">Meerwerk-prijs:</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={mwPrijs}
              onChange={(e) => setMwPrijs(parseFloat(e.target.value) || 0)}
              className="border border-border rounded px-2 py-1 w-24 text-right tabular-nums"
              placeholder="0.00"
            />
            <span className="text-xs">€/{p.parsedItem.eenheid || 'eenheid'}</span>
            <button className="btn-accent btn-sm" disabled={mwPrijs <= 0} onClick={() => onMeerwerk(mwPrijs)}>+ Meerwerk</button>
          </>
        )}
        <button className="btn-ghost btn-sm" onClick={onSkip}>Negeer</button>
      </div>
    </div>
  );
}

interface ManualAddRowProps {
  pricelistItems: PricelistItem[];
  onAdd: (plItemId: string, hoeveelheid: number) => void;
  onAddFree: (line: MatchedLine) => void;
}

function ManualAddRow({ pricelistItems, onAdd, onAddFree }: ManualAddRowProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'pricelist' | 'free'>('pricelist');
  const [plItemId, setPlItemId] = useState('');
  const [qty, setQty] = useState(0);
  // Free-invoer velden
  const [omschrijving, setOmschrijving] = useState('');
  const [eenheid, setEenheid] = useState('STUK');
  const [prijs, setPrijs] = useState(0);

  const reset = () => {
    setPlItemId(''); setQty(0); setOmschrijving(''); setEenheid('STUK'); setPrijs(0); setOpen(false);
  };

  if (!open) {
    return (
      <button className="btn-ghost btn-sm mt-2 mb-4" onClick={() => setOpen(true)}>
        + Werkpost handmatig toevoegen
      </button>
    );
  }

  return (
    <div className="card-flat mt-2 mb-4 space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold text-secondary mr-2">Werkpost toevoegen</span>
        <button className={`px-2 py-1 rounded ${mode === 'pricelist' ? 'bg-primary text-white' : 'bg-warmgrey'}`} onClick={() => setMode('pricelist')}>uit prijslijst</button>
        <button className={`px-2 py-1 rounded ${mode === 'free' ? 'bg-accent text-white' : 'bg-warmgrey'}`} onClick={() => setMode('free')}>vrije invoer</button>
        <button className="btn-ghost btn-sm ml-auto" onClick={reset}>✕ Annuleren</button>
      </div>
      {mode === 'pricelist' ? (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <PricelistItemPicker
              items={pricelistItems}
              value={plItemId}
              onChange={setPlItemId}
              placeholder="Zoek arbeid in prijslijst…"
              size="sm"
            />
          </div>
          <input
            type="number"
            step="0.01"
            min="0"
            value={qty}
            onChange={(e) => setQty(parseFloat(e.target.value) || 0)}
            className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            placeholder="Hoeveelheid"
          />
          <button
            className="btn-primary btn-sm"
            disabled={!plItemId || qty <= 0}
            onClick={() => { onAdd(plItemId, qty); reset(); }}
          >
            ✓ Toevoegen
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            className="input flex-1 min-w-[260px]"
            placeholder="Omschrijving van het werk…"
            value={omschrijving}
            onChange={(e) => setOmschrijving(e.target.value)}
          />
          <input
            type="number"
            step="0.01"
            min="0"
            value={qty}
            onChange={(e) => setQty(parseFloat(e.target.value) || 0)}
            className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            placeholder="aantal"
          />
          <input
            type="text"
            className="border border-border rounded px-2 py-2 w-16 text-left uppercase"
            value={eenheid}
            onChange={(e) => setEenheid(e.target.value.toUpperCase())}
          />
          <span>×</span>
          <span>€</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={prijs}
            onChange={(e) => setPrijs(parseFloat(e.target.value) || 0)}
            className="border border-border rounded px-2 py-2 w-24 text-right tabular-nums"
            placeholder="prijs"
          />
          <button
            className="btn-primary btn-sm"
            disabled={!omschrijving || qty <= 0}
            onClick={() => {
              const line: MatchedLine = {
                id: 'ml_' + Math.random().toString(36).slice(2, 10),
                naamArbeid: omschrijving,
                eenheid,
                prijs,
                hoeveelheid: qty,
                subtotaal: prijs * qty,
                consumedParsedItemIds: [],
                autoMatched: false,
                subItems: [],
              };
              onAddFree(line);
              reset();
            }}
          >
            ✓ Toevoegen
          </button>
        </div>
      )}
    </div>
  );
}
